import React, { useState, useEffect } from 'react';
import { CollapsiblePanel } from '../CollapsiblePanel';
import './CombinedSessionsPanel.css';
import { shortenStellarAddress } from '../utils/stellarDisplay';

interface Session {
  sessionId: number;
  player1: string | null;
  player2: string | null;
  state: string;
  p1CellId?: number;
  p2CellId?: number;
  p1Country?: number;
  p2Country?: number;
  createdLedger?: number;
  players?: Array<{ address: string; cell_id: number; asset_tag: any; country: number; location_proof?: any }>;
  isPublic?: boolean;
  is_public?: boolean;
  trustGroupIds?: number[];
}

interface CombinedSessionsPanelProps {
  userCurrentSession: number | null;
  activeSessions: Session[];
  walletAddress: string | null;
  onJoinSession: (sessionId: number) => Promise<void>;
  onEndSession: (sessionId: number, isPlayer1: boolean, isPlayer2: boolean, sessionState: string) => Promise<void>;
  onViewSessionDetails: () => void;
  contractClient: any;
  isAdmin?: boolean;
  onDeleteSession?: (sessionId: number) => Promise<void>;
  onDeleteAllSessions?: () => Promise<void>;
  /** Narrow the map to only members of the given trust group(s). */
  onShowOnlyTrustGroups?: (groupIds: number[]) => void;
}

export const CombinedSessionsPanel: React.FC<CombinedSessionsPanelProps> = ({
  userCurrentSession,
  activeSessions,
  walletAddress,
  onJoinSession,
  onEndSession,
  onViewSessionDetails,
  contractClient,
  isAdmin = false,
  onDeleteSession,
  onDeleteAllSessions,
  onShowOnlyTrustGroups,
}) => {
  const [minimized, setMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<'your' | 'other'>('your');
  const [yourSessionData, setYourSessionData] = useState<Session | null>(null);
  const [isLoadingYourSession, setIsLoadingYourSession] = useState(false);

  // Find session in activeSessions first
  const yourSessionFromList = userCurrentSession !== null 
    ? activeSessions.find(s => s.sessionId === userCurrentSession)
    : null;

  // Track if we've already attempted to fetch this session
  const hasFetchedSessionRef = React.useRef<number | null>(null);

  // If not found in activeSessions but userCurrentSession is set, fetch it directly
  useEffect(() => {
    // Only fetch if we haven't already fetched this session ID
    if (userCurrentSession !== null && !yourSessionFromList && contractClient && 
        !isLoadingYourSession && hasFetchedSessionRef.current !== userCurrentSession) {
      setIsLoadingYourSession(true);
      hasFetchedSessionRef.current = userCurrentSession;
      contractClient.getSession(userCurrentSession)
        .then((session: any) => {
          if (session) {
            setYourSessionData({
              sessionId: userCurrentSession,
              player1: session.player1 || null,
              player2: session.player2 || null,
              state: session.state || 'Unknown',
              p1CellId: session.p1CellId || session.p1_cell_id,
              p2CellId: session.p2CellId || session.p2_cell_id,
              p1Country: session.p1Country || session.p1_country,
              p2Country: session.p2Country || session.p2_country,
              createdLedger: session.createdLedger || session.created_ledger,
              players: session.players,
              isPublic: session.isPublic || session.is_public,
              is_public: session.is_public || session.isPublic,
            });
          }
          setIsLoadingYourSession(false);
        })
        .catch((error: any) => {
          // console.error('[CombinedSessionsPanel] Failed to fetch session:', error);
          // setIsLoadingYourSession(false);
          // Reset ref on error so we can retry
          hasFetchedSessionRef.current = null;
        });
    } else if (yourSessionFromList) {
      // Clear fetched data if session is now in activeSessions
      setYourSessionData(null);
      // Reset ref when session is found in list
      if (hasFetchedSessionRef.current === userCurrentSession) {
        hasFetchedSessionRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCurrentSession, yourSessionFromList, contractClient]);

  // Use session from activeSessions if available, otherwise use fetched data
  const yourSession = yourSessionFromList || yourSessionData;

  const otherSessions = activeSessions.filter(
    s => s.sessionId !== userCurrentSession && s.state !== 'Ended'
  );

  const isSessionPublic = (s: Session) => !!(s.isPublic || s.is_public);
  const sessionGroupIds = (s: Session) =>
    Array.isArray(s.trustGroupIds) ? s.trustGroupIds.filter((n) => Number.isInteger(n)) : [];

  // Renders the "who can see this session" badge + optional
  // "show only this trust group on the map" toggle. The scope shown
  // here is the same one that governs the live broadcast visibility.
  const renderScope = (s: Session) => {
    if (isSessionPublic(s)) {
      return <span style={{ color: '#28a745', fontSize: '10px', fontWeight: 600 }}>🌐 Public</span>;
    }
    const groups = sessionGroupIds(s);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ color: '#b45309', fontSize: '10px', fontWeight: 600 }}>
          🔒 {groups.length > 0
            ? `Trust group${groups.length > 1 ? 's' : ''}: ${groups.map((g) => `#${g}`).join(', ')}`
            : 'Private'}
        </span>
        {groups.length > 0 && onShowOnlyTrustGroups && (
          <button
            type="button"
            onClick={() => onShowOnlyTrustGroups(groups)}
            style={{
              padding: '2px 8px', fontSize: '10px', borderRadius: 999,
              border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', color: '#334155',
            }}
            title="Show only members of this session's trust group(s) on the map"
          >
            Show only on map
          </button>
        )}
      </span>
    );
  };

  // Count sessions: include user's session if it's not in activeSessions
  const sessionCount = activeSessions.filter(s => s.state !== 'Ended').length + 
    (userCurrentSession !== null && !activeSessions.find(s => s.sessionId === userCurrentSession) ? 1 : 0);
  
  // Wallets in non-ended sessions (public `players` array or private two-slot model)
  const activeWalletCount = activeSessions.filter(s => s.state !== 'Ended').reduce((count, s) => {
    if (s.players && s.players.length > 0) {
      return count + s.players.length;
    }
    if (s.player1) count++;
    if (s.player2) count++;
    return count;
  }, 0) +
  (userCurrentSession !== null && !activeSessions.find(s => s.sessionId === userCurrentSession) && yourSession ?
    (yourSession.players && yourSession.players.length > 0 ? yourSession.players.length :
     ((yourSession.player1 ? 1 : 0) + (yourSession.player2 ? 1 : 0))) : 0);

  return (
    <CollapsiblePanel
      title={`Sessions (${sessionCount} active, ${activeWalletCount} wallets)`}
      minimized={minimized}
      onToggleMinimize={() => setMinimized(!minimized)}
      className="combined-sessions-panel"
      style={{ marginTop: '8px', padding: '20px' }}
    >
      {!minimized && (
        <>
          {/* Tab Navigation */}
          <div className="sessions-tabs">
            <button
              className={`session-tab ${activeTab === 'your' ? 'active' : ''}`}
              onClick={() => setActiveTab('your')}
              disabled={userCurrentSession === null}
            >
              Your Session {userCurrentSession !== null && `#${userCurrentSession}`}
            </button>
            <button
              className={`session-tab ${activeTab === 'other' ? 'active' : ''}`}
              onClick={() => setActiveTab('other')}
            >
              Other Sessions ({otherSessions.length})
            </button>
          </div>

          {/* Your Session Tab */}
          {activeTab === 'your' && userCurrentSession !== null && (
            <div className="session-tab-content">
              {isLoadingYourSession && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Loading session details...
                </div>
              )}
              {!isLoadingYourSession && !yourSession && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  Session #{userCurrentSession} not found. It may have ended or been deleted.
                </div>
              )}
              {!isLoadingYourSession && yourSession && (
              <div className="session-header-section" style={{ backgroundColor: '#FFD700', color: '#000', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                  <div><strong>Session #{userCurrentSession}</strong></div>
                  <div style={{ marginTop: '4px' }}><strong>Visibility:</strong> {renderScope(yourSession)}</div>
                  <div style={{ marginTop: '4px' }}><strong>Status:</strong> {yourSession.state || 'Active'}</div>
                  {yourSession?.players && yourSession.players.length > 0 ? (
                    <div style={{ marginTop: '4px' }}>
                      <strong>Wallets in session ({yourSession.players.length}):</strong>
                      {yourSession.players.map((p: any, idx: number) => (
                        <div key={idx} style={{ marginTop: '2px', fontSize: '11px' }}>
                          {shortenStellarAddress(p.address)}
                          {p.cell_id && <span style={{ color: '#666' }}> (Cell: {p.cell_id})</span>}
                          {p.country && <span style={{ color: '#666' }}> (Country: {p.country})</span>}
                          {walletAddress && p.address === walletAddress && <span style={{ color: '#28a745', fontWeight: 'bold' }}> (You)</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {yourSession?.player1 && (
                        <div style={{ marginTop: '4px' }}>
                          <strong>Wallet 1:</strong> {shortenStellarAddress(yourSession.player1)}
                          {yourSession.p1CellId && <span style={{ color: '#666', fontSize: '10px' }}> (Cell: {yourSession.p1CellId})</span>}
                          {yourSession.p1Country && <span style={{ color: '#666', fontSize: '10px' }}> (Country: {yourSession.p1Country})</span>}
                        </div>
                      )}
                      {yourSession?.player2 && (
                        <div style={{ marginTop: '4px' }}>
                          <strong>Wallet 2:</strong> {shortenStellarAddress(yourSession.player2)}
                          {yourSession.p2CellId && <span style={{ color: '#666', fontSize: '10px' }}> (Cell: {yourSession.p2CellId})</span>}
                          {yourSession.p2Country && <span style={{ color: '#666', fontSize: '10px' }}> (Country: {yourSession.p2Country})</span>}
                        </div>
                      )}
                    </>
                  )}
                  {yourSession.createdLedger && (
                    <div style={{ color: '#666', fontSize: '10px', marginTop: '4px' }}>
                      Created at ledger: {yourSession.createdLedger}
                    </div>
                  )}
                </div>
                
                {walletAddress && (
                  (yourSession.player1 === walletAddress || yourSession.player2 === walletAddress) ||
                  (yourSession.players && yourSession.players.some((p: any) => p.address === walletAddress))
                ) && (
                  <>
                    {(() => {
                      const isPlayer1 = yourSession.player1 === walletAddress;
                      const isPlayer2 = yourSession.player2 === walletAddress;
                      return (
                        <button
                          className="primary-button"
                  onClick={async () => {
                    await onEndSession(userCurrentSession, isPlayer1, isPlayer2, yourSession.state);
                  }}
                          style={{ padding: '6px 12px', fontSize: '11px', backgroundColor: '#dc3545', color: '#fff', width: '100%', marginBottom: '8px' }}
                        >
                          End Session
                        </button>
                      );
                    })()}
                    <button
                      className="primary-button"
                      onClick={onViewSessionDetails}
                      style={{ padding: '6px 12px', fontSize: '11px', backgroundColor: '#000', color: '#FFD700', width: '100%' }}
                    >
                      View Session Details
                    </button>
                  </>
                )}
              </div>
              )}
            </div>
          )}

          {activeTab === 'your' && userCurrentSession === null && (
            <div className="session-tab-content">
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                You are not currently in a session.
              </div>
            </div>
          )}

          {/* Other Sessions Tab */}
          {activeTab === 'other' && (
            <div className="session-tab-content">
              <div className="sessions-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {otherSessions.length > 0 ? (
                  otherSessions.map(session => (
                    <div key={session.sessionId} style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', marginBottom: '8px', fontSize: '12px' }}>
                      <div><strong>Session #{session.sessionId}</strong></div>
                      <div style={{ marginTop: '4px' }}><strong>Visibility:</strong> {renderScope(session)}</div>
                      <div style={{ marginTop: '4px' }}><strong>Status:</strong> {session.state || 'Unknown'}</div>
                      <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {session.players && session.players.length > 0 ? (
                          <div>
                            <strong>Wallets in session ({session.players.length}):</strong>
                            {session.players.map((p: any, idx: number) => (
                              <div key={idx} style={{ marginTop: '2px', fontSize: '11px' }}>
                                {shortenStellarAddress(p.address)}
                                {p.cell_id && <span style={{ color: '#666' }}> (Cell: {p.cell_id})</span>}
                                {p.country && <span style={{ color: '#666' }}> (Country: {p.country})</span>}
                                {walletAddress && p.address === walletAddress && <span style={{ color: '#28a745', fontWeight: 'bold' }}> (You)</span>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                            <div>
                              <strong>Wallet 1:</strong> {session.player1 ? (
                                <span>
                                  {shortenStellarAddress(session.player1)}
                                  {session.p1CellId && <span style={{ color: '#666', fontSize: '10px' }}> (Cell: {session.p1CellId})</span>}
                                  {session.p1Country && <span style={{ color: '#666', fontSize: '10px' }}> (Country: {session.p1Country})</span>}
                                </span>
                              ) : 'Waiting...'}
                            </div>
                            <div>
                              <strong>Wallet 2:</strong> {session.player2 ? (
                                <span>
                                  {shortenStellarAddress(session.player2)}
                                  {session.p2CellId && <span style={{ color: '#666', fontSize: '10px' }}> (Cell: {session.p2CellId})</span>}
                                  {session.p2Country && <span style={{ color: '#666', fontSize: '10px' }}> (Country: {session.p2Country})</span>}
                                </span>
                              ) : 'Waiting...'}
                            </div>
                          </>
                        )}
                        <div><strong>State:</strong> {session.state}</div>
                        {session.createdLedger && (
                          <div style={{ color: '#666', fontSize: '10px' }}>
                            Created at ledger: {session.createdLedger}
                          </div>
                        )}
                      </div>
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {session.state === 'Waiting' && (
                          <button
                            className="primary-button"
                            onClick={() => onJoinSession(session.sessionId)}
                            style={{ padding: '6px 12px', fontSize: '11px', width: '100%' }}
                            disabled={userCurrentSession !== null}
                          >
                            {userCurrentSession !== null ? 'Already in a Session' : 'Join Session'}
                          </button>
                        )}
                        {isAdmin && onDeleteSession && (
                          <button
                            className="primary-button"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete session #${session.sessionId}? This action cannot be undone.`)) {
                                await onDeleteSession(session.sessionId);
                              }
                            }}
                            style={{ padding: '6px 12px', fontSize: '11px', width: '100%', backgroundColor: '#dc3545', color: '#fff' }}
                          >
                            🗑️ Delete Session (Admin)
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    No other active sessions.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </CollapsiblePanel>
  );
};
